import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CopyableLink } from '@/components/CopyableLink'
import {
  useBackofficeTenant,
  useCreateBackofficeTenant,
  useUpdateBackofficeTenant,
  type CreateTenantInput,
  type UpdateTenantInput,
} from '@/hooks/backoffice/useBackofficeTenants'
import type { CreateTenantResult } from '@/types/backoffice'

interface FormState {
  name: string
  cnpj: string
  primaryColor: string
  adminName: string
  adminEmail: string
}

export function TenantFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id

  const { data: existing } = useBackofficeTenant(id ?? '')
  const create = useCreateBackofficeTenant()
  const update = useUpdateBackofficeTenant(id ?? '')

  const [apiError, setApiError] = useState<string | null>(null)
  const [createdInvite, setCreatedInvite] = useState<CreateTenantResult['invite'] | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormState>({
    defaultValues: { name: '', cnpj: '', primaryColor: '#2563eb', adminName: '', adminEmail: '' },
  })

  useEffect(() => {
    if (isEdit && existing) {
      reset({
        name: existing.name,
        cnpj: existing.cnpj,
        primaryColor: existing.primaryColor,
        adminName: '',
        adminEmail: '',
      })
    }
  }, [isEdit, existing, reset])

  async function onSubmit(data: FormState) {
    setApiError(null)
    try {
      if (isEdit) {
        const payload: UpdateTenantInput = {
          name: data.name,
          cnpj: data.cnpj.replace(/\D/g, ''),
          primaryColor: data.primaryColor,
        }
        await update.mutateAsync(payload)
        navigate(`/backoffice/tenants/${id}`)
      } else {
        const payload: CreateTenantInput = {
          name: data.name,
          cnpj: data.cnpj.replace(/\D/g, ''),
          primaryColor: data.primaryColor,
          adminName: data.adminName,
          adminEmail: data.adminEmail,
        }
        const res = await create.mutateAsync(payload)
        setCreatedInvite(res.invite)
      }
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data?.error?.message as string | undefined) : undefined
      setApiError(msg ?? 'Erro ao salvar.')
    }
  }

  if (createdInvite) {
    return (
      <div className="mx-auto max-w-xl space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Transportadora criada</h1>
          <p className="text-sm text-muted-foreground">
            Envie este link para o admin <span className="font-medium">{createdInvite.email}</span> concluir o cadastro.
          </p>
        </div>
        <div className="space-y-3 rounded-lg border border-border bg-card p-4">
          <p className="text-sm text-foreground">
            <span className="font-medium">Admin convidado:</span> {createdInvite.adminName} ({createdInvite.email})
          </p>
          <CopyableLink
            url={createdInvite.acceptUrl}
            hint={`Expira em ${new Date(createdInvite.expiresAt).toLocaleString('pt-BR')}`}
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/backoffice/tenants')}>
            Voltar à lista
          </Button>
          <Button onClick={() => setCreatedInvite(null)}>Cadastrar outra</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? 'Editar transportadora' : 'Nova transportadora'}
        </h1>
        {!isEdit && (
          <p className="text-sm text-muted-foreground">
            Será criado um convite para o admin inicial completar o cadastro.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Nome / Razão social</Label>
          <Input id="name" {...register('name', { required: 'Obrigatório', minLength: 2 })} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cnpj">CNPJ (apenas números)</Label>
          <Input id="cnpj" maxLength={18} {...register('cnpj', { required: 'Obrigatório', pattern: { value: /^\d{14}$/, message: '14 dígitos' } })} />
          {errors.cnpj && <p className="text-xs text-destructive">{errors.cnpj.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="primaryColor">Cor primária</Label>
          <div className="flex gap-2">
            <Input id="primaryColor" type="color" className="w-20 p-1" {...register('primaryColor')} />
            <Input className="flex-1" {...register('primaryColor', { pattern: /^#[0-9a-fA-F]{6}$/ })} />
          </div>
        </div>

        {!isEdit && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="adminName">Nome do admin inicial</Label>
              <Input id="adminName" {...register('adminName', { required: 'Obrigatório', minLength: 2 })} />
              {errors.adminName && <p className="text-xs text-destructive">{errors.adminName.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="adminEmail">Email do admin inicial</Label>
              <Input
                id="adminEmail"
                type="email"
                {...register('adminEmail', {
                  required: 'Obrigatório',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                })}
              />
              {errors.adminEmail && <p className="text-xs text-destructive">{errors.adminEmail.message}</p>}
            </div>
          </>
        )}

        {apiError && (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {apiError}
          </p>
        )}

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => navigate('/backoffice/tenants')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar transportadora'}
          </Button>
        </div>
      </form>
    </div>
  )
}
